import { getService } from '@databases/service-container';
import { useCallback, useSyncExternalStore } from 'react';

import { databasesPingRegistry } from './ping-registry.ts';

export type FieldSelectionState = 'none' | 'start' | 'middle' | 'end' | 'single';

export interface UseFieldSelectionStateOptions {
  fieldId: string;
  previousFieldId?: string;
  nextFieldId?: string;
}

/**
 * Reads selected field IDs once to derive one header's contiguous state and
 * refreshes only when that column's scoped invalidation fires. Neighbour IDs
 * are supplied by the layout so no row or cell subscribes to selection changes.
 */
export function useFieldSelectionState({
  fieldId,
  previousFieldId,
  nextFieldId
}: UseFieldSelectionStateOptions): FieldSelectionState {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const pingKey = databasesPingRegistry.key('column', { fieldId });

      return databasesPingRegistry.subscribeTo(
        pingKey,
        'selectionChanged',
        onStoreChange
      );
    },
    [fieldId]
  );

  const getSnapshot = useCallback((): FieldSelectionState => {
    const selectedFieldIds = new Set(getService('SelectionsApi').getSelectedFieldIds());

    if (!selectedFieldIds.has(fieldId)) {
      return 'none';
    }

    const hasPrevious =
      previousFieldId !== undefined && selectedFieldIds.has(previousFieldId);
    const hasNext = nextFieldId !== undefined && selectedFieldIds.has(nextFieldId);

    if (!hasPrevious && !hasNext) {
      return 'single';
    }

    if (!hasPrevious) {
      return 'start';
    }

    if (!hasNext) {
      return 'end';
    }

    return 'middle';
  }, [fieldId, nextFieldId, previousFieldId]);

  return useSyncExternalStore(subscribe, getSnapshot);
}
